import { useEffect, useState } from 'react';
import type { CardBankReport, PaymentsReport, ReconciliationAct, RegisterRef, ReportGroup, SalesByPeriodReport, StockMovementReport } from '@kassa/shared';
import { api, ApiError } from '../api';
import type { ReportQuery } from '../api/reports';
import { formatDate, formatMoney, formatQty } from '../format';

// KT-90 hesabat tabları: ödəniş növləri, dövr üzrə satış, anbar hərəkəti, qarşılıqlı hesablaşma aktı.
// Məbləğlər API-dən onluq sətir gəlir — yalnız göstərmək üçün Number-ə çevrilir, hesab aparılmır.

type Excel = (path: string, params: Record<string, string | undefined>, format?: 'xlsx' | 'pdf') => void;
const m = (v: string) => formatMoney(Number(v));
const fail = (set: (s: string | null) => void) => (err: unknown) => set(err instanceof ApiError ? err.message : 'Hesabat yüklənmədi');

/** Ödəniş növləri: satış / qaytarma / xalis, kassaya daxil olan pul, uyğunsuzluq; kassa və «bu növbə» süzgəci. */
export function PaymentsTab({ query, excel }: { query: ReportQuery; excel: Excel }) {
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [registerId, setRegisterId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [data, setData] = useState<PaymentsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.shiftRegisters().then(setRegisters).catch(() => setRegisters([]));
  }, []);
  const q = { ...query, registerId: registerId || undefined, shiftId: shiftId || undefined };
  const key = JSON.stringify(q);
  useEffect(() => {
    setError(null);
    api.reportPayments(q).then(setData).catch(fail(setError));
  }, [key]);

  const thisShift = () => {
    if (!registerId) return;
    api
      .shiftCurrent(registerId)
      .then((s) => (s ? setShiftId(s.shiftId) : setError('Bu kassada açıq növbə yoxdur')))
      .catch(fail(setError));
  };

  const rows: [string, keyof PaymentsReport['net']][] = [
    ['Nağd', 'cash'],
    ['Kart', 'card'],
    ['Köçürmə', 'transfer'],
    ['Bonus (pul deyil)', 'bonus'],
    ['Nisyə', 'credit'],
  ];
  return (
    <>
      <div className="toolbar">
        {registers.length > 0 && (
          <select aria-label="Kassa" value={registerId} onChange={(e) => (setRegisterId(e.target.value), setShiftId(''))}>
            <option value="">Bütün kassalar</option>
            {registers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.outletName}
              </option>
            ))}
          </select>
        )}
        {registerId && (
          <button type="button" className={shiftId ? 'btn-small is-active' : 'btn-small'} onClick={() => (shiftId ? setShiftId('') : thisShift())}>
            Bu növbə
          </button>
        )}
        <button type="button" className="btn-small" onClick={() => excel('/api/reports/payments', q)}>
          Excel-ə
        </button>
        <button type="button" className="btn-small" onClick={() => excel('/api/reports/payments', q, 'pdf')}>
          PDF
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && (
        <>
          <table className="report-table report-full" tabIndex={0} aria-label="Ödəniş növləri">
            <thead>
              <tr>
                <th>Növ</th>
                <th className="num">Satış ({data.sales.count})</th>
                <th className="num">Qaytarma ({data.returns.count})</th>
                <th className="num">Xalis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, k]) => (
                <tr key={k}>
                  <td>{label}</td>
                  <td className="num">{m(data.sales[k])}</td>
                  <td className="num">{m(data.returns[k])}</td>
                  <td className="num">{m(data.net[k])}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Cəmi (çeklər)</th>
                <th className="num">{m(data.sales.total)}</th>
                <th className="num">{m(data.returns.total)}</th>
                <th className="num">{m(data.net.total)}</th>
              </tr>
              <tr>
                <th colSpan={3}>Kassaya / hesaba pul (nağd + kart + köçürmə)</th>
                <th className="num">{m(data.money)}</th>
              </tr>
            </tfoot>
          </table>
          {data.mismatchCount > 0 ? (
            <div className="bad" role="alert">
              <p>
                Uyğunsuzluq: {data.mismatchCount} çekdə ödəniş növlərinin cəmi çek məbləğinə bərabər deyil.
              </p>
              <ul className="plain-list small">
                {data.mismatches.map((x) => (
                  <li key={x.docId}>
                    {x.displayNo}: çek {m(x.total)}, növlər {m(x.methods)}, fərq {m(x.diff)}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="ok small">Yoxlama: ödəniş növlərinin cəmi çeklərin cəminə bərabərdir.</p>
          )}
        </>
      )}
    </>
  );
}

const GROUPS: [ReportGroup, string][] = [
  ['day', 'Gün'],
  ['month', 'Ay'],
  ['quarter', 'Rüb'],
  ['year', 'İl'],
];

/** Dövr üzrə satış: mal × gün/ay/rüb/il, cəmi sətri və sütunu. */
export function ByPeriodTab({ query, excel }: { query: ReportQuery; excel: Excel }) {
  const [group, setGroup] = useState<ReportGroup>('month');
  const [data, setData] = useState<SalesByPeriodReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = { ...query, group };
  const key = JSON.stringify(q);
  useEffect(() => {
    setError(null);
    api.reportSalesByPeriod(q).then(setData).catch(fail(setError));
  }, [key]);
  return (
    <>
      <div className="toolbar">
        {GROUPS.map(([g, label]) => (
          <button key={g} type="button" className={group === g ? 'btn-small is-active' : 'btn-small'} onClick={() => setGroup(g)}>
            {label}
          </button>
        ))}
        <button type="button" className="btn-small" onClick={() => excel('/api/reports/sales-by-period', q)}>
          Excel-ə
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && (
        <table className="report-table report-full" tabIndex={0} aria-label="Dövr üzrə satış">
          <thead>
            <tr>
              <th>Mal</th>
              {data.periods.map((p) => (
                <th key={p} className="num">
                  {p}
                </th>
              ))}
              <th className="num">Cəmi</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.productId}>
                <td>{r.name}</td>
                {r.cells.map((c, i) => (
                  <td key={data.periods[i]} className="num">
                    {c.amount === '0.00' ? '—' : m(c.amount)}
                  </td>
                ))}
                <td className="num">
                  <strong>{m(r.total.amount)}</strong>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={data.periods.length + 2} className="muted">
                  Bu dövrdə satış yoxdur.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th>Cəmi</th>
              {data.totals.cells.map((c, i) => (
                <th key={data.periods[i]} className="num">
                  {m(c)}
                </th>
              ))}
              <th className="num">{m(data.totals.amount)}</th>
            </tr>
          </tfoot>
        </table>
      )}
    </>
  );
}

/** Anbar hərəkəti: tarix aralığı məcburidir (seçilmiş dövrdən), obyekt. */
export function StockMovementTab({ from, to, outletId, excel }: { from: string; to: string; outletId?: string; excel: Excel }) {
  const [data, setData] = useState<StockMovementReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    api.reportStockMovement({ from, to, outletId }).then(setData).catch(fail(setError));
  }, [from, to, outletId]);
  const cols: [keyof StockMovementReport['rows'][number], string][] = [
    ['opening', 'Dövrün əvvəlinə'],
    ['purchase', 'Alış'],
    ['customerReturn', 'Alıcıdan qaytarma'],
    ['sale', 'Satış'],
    ['transferIn', 'Yerdəyişmə giriş'],
    ['transferOut', 'Yerdəyişmə çıxış'],
    ['writeOff', 'Silinmə'],
    ['other', 'Digər'],
    ['closing', 'Dövrün sonuna'],
  ];
  return (
    <>
      <div className="toolbar">
        <p className="muted small">Miqdar üzrə · tranzit («yolda») anbar daxil deyil</p>
        <button type="button" className="btn-small" onClick={() => excel('/api/reports/stock-movement', { from, to, outletId })}>
          Excel-ə
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && (
        <table className="report-table report-full" tabIndex={0} aria-label="Anbar hərəkəti">
          <thead>
            <tr>
              <th>Mal</th>
              {cols.map(([k, label]) => (
                <th key={k} className="num">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.productId}>
                <td>
                  {r.name} <span className="muted small">{r.unit}</span>
                </td>
                {cols.map(([k]) => (
                  <td key={k} className={k === 'closing' && Number(r[k]) < 0 ? 'num bad' : 'num'}>
                    {formatQty(r[k] as number)}
                  </td>
                ))}
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={cols.length + 1} className="muted">
                  Bu dövrdə hərəkət yoxdur.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

const ACT_TYPE: Record<ReconciliationAct['rows'][number]['type'], string> = { sale: 'Satış', purchase: 'Alış', return: 'Qaytarma', payment: 'Ödəniş', other: 'Digər' };

/** Qarşılıqlı hesablaşma aktı: qarşı tərəf seçilir; 3 kart (əvvələ, dövriyyə, sona); sənəd sətrində mallar açılır. */
export function ActTab({ from, to, excel }: { from: string; to: string; excel: Excel }) {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [cpId, setCpId] = useState('');
  const [data, setData] = useState<ReconciliationAct | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .customers({ q: search || undefined, limit: 20 })
        .then((r) => setOptions(r.items.map((c) => ({ id: c.id, name: c.name }))))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setData(null);
    setOpen(new Set());
    if (!cpId) return;
    setError(null);
    api.reportReconciliation({ from, to, counterpartyId: cpId }).then(setData).catch(fail(setError));
  }, [cpId, from, to]);

  const toggle = (i: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  return (
    <>
      <div className="toolbar act-toolbar">
        <input type="search" aria-label="Qarşı tərəf axtarışı" placeholder="Ad, telefon, VÖEN" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Qarşı tərəf" value={cpId} onChange={(e) => setCpId(e.target.value)}>
          <option value="">Qarşı tərəf seçin</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        {cpId && (
          <>
            <button type="button" className="btn-small" onClick={() => excel('/api/reports/reconciliation', { from, to, counterpartyId: cpId })}>
              Excel-ə
            </button>
            <button type="button" className="btn-small" onClick={() => excel('/api/reports/reconciliation', { from, to, counterpartyId: cpId }, 'pdf')}>
              PDF
            </button>
          </>
        )}
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && (
        <>
          <div className="act-cards">
            <div className="act-card">
              <span className="muted small">Dövrün əvvəlinə</span>
              <strong>{m(data.opening)}</strong>
            </div>
            <div className="act-card">
              <span className="muted small">Dövriyyə: debet / kredit</span>
              <strong>
                {m(data.debit)} / {m(data.credit)}
              </strong>
            </div>
            <div className="act-card">
              <span className="muted small">Dövrün sonuna</span>
              <strong className={Number(data.closing) < 0 ? 'bad' : undefined}>{m(data.closing)}</strong>
              <span className="muted small">{Number(data.closing) > 0 ? 'bizə borcludur' : Number(data.closing) < 0 ? 'biz borcluyuq' : 'borc yoxdur'}</span>
            </div>
          </div>
          <table className="report-table report-full" tabIndex={0} aria-label="Akt">
            <thead>
              <tr>
                <th>Tarix</th>
                <th>Növ</th>
                <th>Sənəd</th>
                <th>Qeyd</th>
                <th className="num">Debet</th>
                <th className="num">Kredit</th>
                <th className="num">Qalıq</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <ActRow key={i} r={r} open={open.has(i)} onToggle={() => toggle(i)} />
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Bu dövrdə hərəkət yoxdur.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {data.truncated && <p className="muted small">Akt uzundur — yalnız ilk sətirlər göstərilir. Aralığı daraldın.</p>}
        </>
      )}
    </>
  );
}

function ActRow({ r, open, onToggle }: { r: ReconciliationAct['rows'][number]; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr>
        <td>{formatDate(r.at)}</td>
        <td>{ACT_TYPE[r.type]}</td>
        <td>
          {r.lines.length > 0 ? (
            <button type="button" className="cell-link" aria-expanded={open} onClick={onToggle}>
              {open ? '▾' : '▸'} {r.displayNo}
            </button>
          ) : (
            (r.displayNo ?? '—')
          )}
        </td>
        <td>{r.note ?? ''}</td>
        <td className="num">{r.debit === '0.00' ? '' : m(r.debit)}</td>
        <td className="num">{r.credit === '0.00' ? '' : m(r.credit)}</td>
        <td className="num">{m(r.balance)}</td>
      </tr>
      {open &&
        r.lines.map((l, j) => (
          <tr key={j} className="act-line">
            <td />
            <td colSpan={3}>
              {l.name} — {formatQty(l.qty)} × {m(l.price)}
            </td>
            <td className="num muted">{m(l.amount)}</td>
            <td />
            <td />
          </tr>
        ))}
    </>
  );
}

/** Kart → bank (KT-90): terminal × gün, komissiya, hesaba düşmə; gün sonu (batch) fərqi qırmızı. */
export function CardBankTab({ query, excel }: { query: ReportQuery; excel: Excel }) {
  const [data, setData] = useState<CardBankReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(query);
  useEffect(() => {
    setError(null);
    api.reportCardBank(query).then(setData).catch(fail(setError));
  }, [key]);
  return (
    <>
      <div className="toolbar">
        <p className="muted small">Komissiya günün xalis cəmindən; hesaba düşmə — terminalın T+N günü</p>
        <button type="button" className="btn-small" onClick={() => excel('/api/reports/card-bank', query as Record<string, string | undefined>)}>
          Excel-ə
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && (
        <>
          <table className="report-table report-full" tabIndex={0} aria-label="Kart və bank">
            <thead>
              <tr>
                <th>Gün</th>
                <th>Terminal</th>
                <th className="num">Satış</th>
                <th className="num">Qaytarma</th>
                <th className="num">Xalis</th>
                <th className="num">Komissiya</th>
                <th className="num">Hesaba gələcək</th>
                <th>Hesaba düşmə</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={`${r.terminalId ?? '-'}-${r.day}`}>
                  <td>{formatDate(r.day)}</td>
                  <td>
                    {r.terminalName}
                    {r.bank ? <span className="muted small"> · {r.bank}</span> : null}
                  </td>
                  <td className="num">{m(r.sales)}</td>
                  <td className="num">{m(r.refunds)}</td>
                  <td className="num">{m(r.net)}</td>
                  <td className="num">
                    {m(r.fee)} <span className="muted small">({r.feePct}%)</span>
                  </td>
                  <td className="num">{m(r.payout)}</td>
                  <td>{formatDate(r.settleOn)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    Bu dövrdə kart ödənişi yoxdur.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Cəmi</th>
                <th className="num">{m(data.totals.sales)}</th>
                <th className="num">{m(data.totals.refunds)}</th>
                <th className="num">{m(data.totals.net)}</th>
                <th className="num">{m(data.totals.fee)}</th>
                <th className="num">{m(data.totals.payout)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
          {data.batches.length > 0 && (
            <table className="report-table report-full" tabIndex={0} aria-label="Gün sonu">
              <caption>Gün sonu (batch): terminalın cəmi sistemlə</caption>
              <thead>
                <tr>
                  <th>Terminal</th>
                  <th>Batch</th>
                  <th>Bağlanma</th>
                  <th className="num">Terminal</th>
                  <th className="num">Sistem</th>
                  <th className="num">Fərq</th>
                </tr>
              </thead>
              <tbody>
                {data.batches.map((b) => (
                  <tr key={`${b.terminalId}-${b.batchNo}`}>
                    <td>{b.terminalName}</td>
                    <td>{b.batchNo}</td>
                    <td>{formatDate(b.closedAt)}</td>
                    <td className="num">{m(b.total)}</td>
                    <td className="num">{m(b.systemTotal)}</td>
                    <td className={b.diff === '0.00' ? 'num' : 'num bad'}>{m(b.diff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data.batchDiffCount > 0 && (
            <p className="bad" role="alert">
              {data.batchDiffCount} gün sonunda terminalın cəmi sistemlə üst-üstə düşmür — bankla yoxlayın.
            </p>
          )}
        </>
      )}
    </>
  );
}
